import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
  Archive,
  ArrowDown,
  ArrowLeft,
  ChevronRight,
  Pencil,
  Plus,
  Trash2,
  Users,
} from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import {
  SELECTABLE_MARKETING_CATEGORIES,
  type AudienceSummary,
  type CadenceStep,
  type CampaignDetail,
  type MarketingCategory,
  type MarketingChannel,
} from "@shared-core/data/marketing-types";
import {
  canArchiveCampaign,
  canChangeCampaignAudience,
  canDeleteCampaign,
} from "@shared-core/domain/campaign-lifecycle";
import {
  hasSent,
  sendLabel,
  sendRows,
} from "@shared-core/domain/campaign-sends";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import type {
  CampaignGoal,
  CampaignPlan,
  CampaignPlanStep,
} from "@shared-core/data/api-types";
import {
  CAMPAIGN_GOALS,
  CAMPAIGN_GOAL_LABELS,
  type CampaignSendListItem,
  campaignGoalLine,
  campaignSendListItems,
  recipientCountLabel,
  showsCampaignPlan,
  stepAudienceLabel,
} from "@shared-core/domain/direct-mail-campaigns";
import { paths } from "@shared-core/routing/routes";
import { useIsAdmin } from "../app/auth/hooks";
import { CampaignSendDetail } from "../app/components/domain/marketing/CampaignSendDetail";
import {
  ChannelIcon,
  StepStateBadge,
} from "../app/components/domain/marketing/CampaignStepDisplay";
import { MarketingNav } from "../app/components/domain/marketing/MarketingNav";
import {
  CHANNEL_ICONS,
  CHANNEL_LABELS,
  CHANNEL_TARGETS,
  campaignDisplayStatus,
  campaignStatusLabel,
  campaignStatusVariant,
  sendStatusLabel,
  sendStatusVariant,
} from "../app/components/domain/marketing/marketingDisplay";
import { Badge } from "../app/components/primitives/badge";
import { Toggle } from "../app/components/primitives/toggle";
import { Button } from "../app/components/primitives/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../app/components/primitives/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../app/components/primitives/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../app/components/primitives/alert-dialog";
import { Input } from "../app/components/primitives/input";
import { Label } from "../app/components/primitives/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "../app/components/primitives/radio-group";
import { ApiError } from "@shared-core/data/api/errors";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import { AddPeopleDialog } from "./campaign-workspace/AddPeopleDialog";
import { AddPlanStepDialog } from "./campaign-workspace/AddPlanStepDialog";
import { CampaignPeopleSheet } from "./campaign-workspace/CampaignPeopleSheet";
import { PlanStepDetail } from "./campaign-workspace/PlanStepDetail";
import {
  StepTiersEditor,
  TierCheckboxes,
} from "./campaign-workspace/StepTiersEditor";
import { TiersCard } from "./campaign-workspace/TiersCard";

function fmtWindow(start: string, end: string): string {
  if (!start || !end) return "No window set";
  return `${formatDateOrDash(start)} – ${formatDateOrDash(end)}`;
}

/** The actual Mailchimp send date once the campaign has sent; falls back to
 * the CIS activation window for drafts/unsent campaigns, which have no
 * Mailchimp send time yet. Mirrors CampaignsPage's fmtSendDate so the list
 * card and this header never disagree once a campaign has actually sent. */
function fmtSendDate(c: CampaignDetail): string {
  if (c.mailchimp_send_time) {
    return `Sent ${formatDateOrDash(c.mailchimp_send_time)}`;
  }
  return fmtWindow(c.window_start, c.window_end);
}

/** One send's own date, shown beside its status badge. A sent send (the same
 * test as the "Sent" badge and the "N of M sent" count — see hasSent) shows
 * Mailchimp's send date alone, since the badge already says "Sent". An
 * activated send with no Mailchimp send time yet reads "Activated …": tagging
 * the audience can precede the real send by days. Null for a draft. */
function fmtStepSendDate(step: CadenceStep): string | null {
  if (hasSent(step)) return formatDateOrDash(step.sent_at);
  return step.activated_at
    ? `Activated ${formatDateOrDash(step.activated_at)}`
    : null;
}

/** Deliberately excludes "email" — an email step is always either a tranche
 * (set up at creation, or added with "Add tranche") or a reminder (added via
 * the "Add reminder" action on its tranche's row), never a generic added
 * step. See the campaign-projects glossary. */
const CHANNELS: { value: MarketingChannel; label: string }[] = [
  { value: "call", label: "Call" },
  { value: "enclosure", label: "Enclosure" },
  { value: "direct_mail", label: "Direct mail" },
];

/** "Send" (a business/UI term, see the campaign-projects glossary) applies
 * only to email-channel cadence steps — call/enclosure/direct_mail steps
 * are never called a Send and stay in the plain "Cadence" list below. */
function emailSteps(campaign: CampaignDetail): CadenceStep[] {
  return campaign.steps.filter((s) => s.channel === "email");
}

/** The plain "Cadence" list: every non-email step, except that a campaign
 * showing its plan lists its mailings and call rounds with its sends. */
function otherSteps(
  campaign: CampaignDetail,
  showPlan: boolean,
): CadenceStep[] {
  return campaign.steps.filter(
    (s) => s.channel !== "email" && !(showPlan && isNamedChannel(s.channel)),
  );
}

/** A mailing or a call round: named, and on its own date. */
function isNamedChannel(channel: MarketingChannel): boolean {
  return channel === "direct_mail" || channel === "call";
}

/** Reads a campaign's plan without letting a failure take the page down: a
 * campaign whose plan can't be read shows as it always has. */
async function loadPlanOrNull(
  load: () => Promise<CampaignPlan | null>,
  campaignId: string,
): Promise<CampaignPlan | null> {
  try {
    return await load();
  } catch (e) {
    console.error("marketing.campaign.plan.load_failed", {
      campaign_id: campaignId,
      exc_type: e instanceof Error ? e.name : typeof e,
    });
    return null;
  }
}

export function CampaignWorkspacePage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const dataAdapter = useDataAdapter();
  const [campaign, setCampaign] = useState<CampaignDetail | null>(null);
  // Tiers, mailings and call rounds (direct-mail campaigns): shown only for
  // a campaign that has a goal, tiers, or a mailing or call round, so an
  // email campaign from before goals looks exactly as it always has.
  const [plan, setPlan] = useState<CampaignPlan | null>(null);
  const [addPeopleOpen, setAddPeopleOpen] = useState(false);
  const [peopleSheetOpen, setPeopleSheetOpen] = useState(false);
  const [peopleRefreshKey, setPeopleRefreshKey] = useState(0);
  const [addedPeopleMessage, setAddedPeopleMessage] = useState<string | null>(
    null,
  );
  const [addPlanStepOpen, setAddPlanStepOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [editStep, setEditStep] = useState<CadenceStep | null>(null);
  const [editCampaignOpen, setEditCampaignOpen] = useState(false);
  const [editNameInput, setEditNameInput] = useState("");
  const [editGoalInput, setEditGoalInput] = useState<CampaignGoal | "">("");
  const [editDescriptionInput, setEditDescriptionInput] = useState("");
  // Comma-separated in the UI, parsed to a string[] on save. Optional —
  // identifies the specific product this campaign promotes (e.g. "advent,
  // advent calendar") so the Results page can scope commercial outcomes to
  // orders for that product rather than any order.
  const [editProductKeywordsInput, setEditProductKeywordsInput] = useState("");
  const [editMarketingCategories, setEditMarketingCategories] = useState<
    MarketingCategory[]
  >([]);
  // Only editable once every email send has gone out (see
  // canChangeCampaignAudience) — e.g. re-pointing a legacy import whose real
  // audience never existed in CIS.
  const [editAudienceId, setEditAudienceId] = useState("");
  const [audienceOptions, setAudienceOptions] = useState<AudienceSummary[]>([]);
  const [savingCampaignInfo, setSavingCampaignInfo] = useState(false);
  const [editCampaignError, setEditCampaignError] = useState<string | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [savingStep, setSavingStep] = useState(false);
  const [deletingStep, setDeletingStep] = useState(false);

  // Edit-step controlled form state (mirrors editStep, reset when drawer
  // opens) — scoped to the generic "Cadence" list (non-email steps); a
  // send's own timing/variant editing is out of scope for this pass.
  const [editOffsetDays, setEditOffsetDays] = useState("0");
  const [editVariant, setEditVariant] = useState("");
  // A mailing's or call round's own name and date.
  const [editName, setEditName] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editStepError, setEditStepError] = useState<string | null>(null);

  // Project-level tracking — end date only now; Mailchimp linkage is
  // per-send (see CampaignSendDetail).
  const [endDateInput, setEndDateInput] = useState("");
  const [savingTracking, setSavingTracking] = useState(false);
  const [trackingError, setTrackingError] = useState<string | null>(null);
  const [trackingSaved, setTrackingSaved] = useState(false);

  // Add-step dialog state
  const [addStepOpen, setAddStepOpen] = useState(false);
  const [addingStep, setAddingStep] = useState(false);
  const [newChannel, setNewChannel] = useState<MarketingChannel>("call");
  const [newOffsetDays, setNewOffsetDays] = useState("0");
  const [newVariant, setNewVariant] = useState("");
  const [newName, setNewName] = useState("");
  const [addStepError, setAddStepError] = useState<string | null>(null);

  // Master-detail send list — which rows are expanded, and reminder
  // generation in flight.
  const [expandedSendIds, setExpandedSendIds] = useState<Set<string>>(
    new Set(),
  );
  const [generatingReminderStepId, setGeneratingReminderStepId] = useState<
    string | null
  >(null);
  const [generateReminderError, setGenerateReminderError] = useState<
    string | null
  >(null);
  const [addingReminderForStepId, setAddingReminderForStepId] = useState<
    string | null
  >(null);
  const [addReminderError, setAddReminderError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    setError(false);
    try {
      const [res, campaignPlan] = await Promise.all([
        dataAdapter.getMarketingCampaign(campaignId),
        loadPlanOrNull(
          () => dataAdapter.getCampaignPlan(campaignId),
          campaignId,
        ),
      ]);
      setCampaign(res);
      setPlan(campaignPlan);
      setEndDateInput(res?.window_end ?? "");
    } catch (e) {
      console.error("marketing.campaign.load.failed", e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [dataAdapter, campaignId]);

  useEffect(() => {
    void load();
  }, [load]);

  // A step-scoped mutation (activate, set-mailchimp-id, generate-reminder)
  // can change the project's own denormalized rollup_status server-side —
  // reload the whole campaign afterward rather than hand-patching local
  // state, the same mutate-then-reload pattern used elsewhere in this app.
  // Silent (no loading flash): this refreshes state the user is already
  // looking at, not an initial load.
  const reloadCampaign = useCallback(async () => {
    if (!campaignId) return;
    try {
      const [res, campaignPlan] = await Promise.all([
        dataAdapter.getMarketingCampaign(campaignId),
        loadPlanOrNull(
          () => dataAdapter.getCampaignPlan(campaignId),
          campaignId,
        ),
      ]);
      setCampaign(res);
      setPlan(campaignPlan);
    } catch (e) {
      console.error("marketing.campaign.reload.failed", e);
    }
  }, [dataAdapter, campaignId]);

  // Admin-only repair for a project consolidated before consolidation
  // ordered by send date (the staging Advent Calendar project): rewrites
  // tranche positions into real Mailchimp send order, then reloads so the
  // "Tranche N" labels below reflect it.
  const isAdmin = useIsAdmin();
  const [reorderingSends, setReorderingSends] = useState(false);
  const [reorderSendsMessage, setReorderSendsMessage] = useState<string | null>(
    null,
  );
  const [reorderSendsError, setReorderSendsError] = useState<string | null>(
    null,
  );

  async function handleReorderSendsBySendDate() {
    if (!campaignId) return;
    setReorderingSends(true);
    setReorderSendsError(null);
    setReorderSendsMessage(null);
    try {
      const result = await dataAdapter.reorderSendsBySendDate(campaignId);
      const undated = result.undated_sent_step_ids.length;
      setReorderSendsMessage(
        (result.moved_step_count > 0
          ? "Tranches re-ordered by send date."
          : "Already in send-date order — nothing to change.") +
          (undated > 0
            ? ` ${undated} sent send${undated === 1 ? " has" : "s have"} no send date yet — pull engagement from Mailchimp on ${undated === 1 ? "it" : "them"}, then run this again.`
            : ""),
      );
      await reloadCampaign();
    } catch (e) {
      console.error("marketing.campaign.reorder_sends.failed", e);
      setReorderSendsError("Could not re-order the sends. Please try again.");
    } finally {
      setReorderingSends(false);
    }
  }

  function toggleSendExpanded(stepId: string) {
    setExpandedSendIds((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) next.delete(stepId);
      else next.add(stepId);
      return next;
    });
  }

  // `generate_reminder_send`'s own step_id parameter identifies the
  // *original tranche* step (see the module spec) — reminderStepId here is
  // only for the button's own loading-state tracking, not what's sent.
  async function handleGenerateReminder(
    reminderStepId: string,
    trancheStepId: string,
  ) {
    if (!campaignId) return;
    setGeneratingReminderStepId(reminderStepId);
    setGenerateReminderError(null);
    try {
      await dataAdapter.generateReminderSend(campaignId, trancheStepId);
      await reloadCampaign();
    } catch (e) {
      console.error("marketing.campaign.generate_reminder.failed", e);
      setGenerateReminderError(
        "Could not prepare the reminder audience. Please try again.",
      );
    } finally {
      setGeneratingReminderStepId(null);
    }
  }

  // Adds a reminder for a tranche that didn't get one at project creation
  // (e.g. the marketer left "Send a reminder" unticked and changed their
  // mind). Timing defaults server-side to the tranche's own offset plus the
  // module's standard reminder gap — this action doesn't ask for a date.
  async function handleAddReminder(trancheStepId: string) {
    if (!campaignId) return;
    setAddingReminderForStepId(trancheStepId);
    setAddReminderError(null);
    try {
      await dataAdapter.addMarketingCampaignStep(campaignId, {
        channel: "email",
        follows_step_id: trancheStepId,
      });
      await reloadCampaign();
    } catch (e) {
      console.error("marketing.campaign.add_reminder.failed", e);
      setAddReminderError("Could not add a reminder. Please try again.");
    } finally {
      setAddingReminderForStepId(null);
    }
  }

  function handleOpenEditCampaign() {
    if (!campaign) return;
    setEditNameInput(campaign.name);
    setEditGoalInput(campaign.goal ?? "");
    setEditDescriptionInput(campaign.description ?? "");
    setEditProductKeywordsInput((campaign.product_keywords ?? []).join(", "));
    setEditMarketingCategories(campaign.marketing_categories ?? []);
    setEditAudienceId(campaign.audience_id);
    setEditCampaignError(null);
    setEditCampaignOpen(true);
    if (canChangeCampaignAudience(campaign)) {
      dataAdapter
        .listMarketingAudiences({ pageSize: 100 })
        .then((res) => setAudienceOptions(res.items))
        .catch((e) => {
          console.error("marketing.campaign.audience_options.load.failed", e);
          setEditCampaignError(
            "Could not load audiences. The audience can't be changed right now.",
          );
        });
    }
  }

  async function handleSaveCampaignInfo() {
    if (!campaignId) return;
    const name = editNameInput.trim();
    if (!name) {
      setEditCampaignError("Campaign name can't be empty.");
      return;
    }
    setSavingCampaignInfo(true);
    setEditCampaignError(null);
    try {
      const productKeywords = editProductKeywordsInput
        .split(",")
        .map((k) => k.trim())
        .filter(Boolean);
      const isAudienceChanging =
        campaign !== null && editAudienceId !== campaign.audience_id;
      const updated = await dataAdapter.updateMarketingCampaign(campaignId, {
        name,
        ...(editGoalInput !== "" ? { goal: editGoalInput } : {}),
        description: editDescriptionInput.trim(),
        product_keywords: productKeywords,
        marketing_categories: editMarketingCategories,
        ...(isAudienceChanging ? { audience_id: editAudienceId } : {}),
      });
      if (isAudienceChanging) {
        // The audience banner's member/suppressed counts belong to the old
        // audience — reload rather than patch them by hand.
        await reloadCampaign();
      } else {
        setCampaign((prev) =>
          prev
            ? {
                ...prev,
                name: updated.name,
                goal: updated.goal,
                description: updated.description,
                product_keywords: updated.product_keywords,
                marketing_categories: updated.marketing_categories,
              }
            : prev,
        );
      }
      setEditCampaignOpen(false);
    } catch (e) {
      console.error("marketing.campaign.update.failed", {
        status: e instanceof ApiError ? e.status : undefined,
      });
      setEditCampaignError(
        e instanceof ApiError && e.status === 400
          ? e.detail
          : "Could not save changes. Please try again.",
      );
    } finally {
      setSavingCampaignInfo(false);
    }
  }

  async function handleDeleteCampaign() {
    if (!campaignId) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await dataAdapter.deleteMarketingCampaign(campaignId);
      navigate(paths.marketingCampaigns());
    } catch (e) {
      console.error("marketing.campaign.delete.failed", e);
      setDeleteError("Could not delete campaign. Please try again.");
      setDeleting(false);
    }
  }

  async function handleArchiveCampaign() {
    if (!campaignId) return;
    setArchiving(true);
    setArchiveError(null);
    try {
      const updated = await dataAdapter.archiveMarketingCampaign(campaignId);
      setCampaign((prev) =>
        prev ? { ...prev, status: updated.status } : prev,
      );
    } catch (e) {
      console.error("marketing.campaign.archive.failed", e);
      setArchiveError("Could not archive campaign. Please try again.");
    } finally {
      setArchiving(false);
    }
  }

  async function handleSaveTracking() {
    if (!campaignId || !campaign) return;
    const endDate = endDateInput.trim();

    // The backend treats an absent key as "leave alone" and has no clear
    // path — update_campaign keeps the old value on null. So blanking a
    // field that already has a value cannot be honoured: say so rather than
    // reporting a save that silently keeps the old value.
    if (campaign.window_end && !endDate) {
      setTrackingSaved(false);
      setTrackingError(
        "Removing an end date isn't supported yet — type a new value over it instead.",
      );
      return;
    }

    setSavingTracking(true);
    setTrackingError(null);
    setTrackingSaved(false);
    try {
      const patch: { end_date?: string } = {};
      if (endDate) patch.end_date = endDate;

      const updated = await dataAdapter.updateMarketingCampaign(
        campaignId,
        patch,
      );
      setCampaign((prev) =>
        prev ? { ...prev, window_end: updated.window_end } : prev,
      );
      setEndDateInput(updated.window_end ?? "");
      setTrackingSaved(true);
    } catch (e) {
      console.error("marketing.campaign.tracking.save.failed", e);
      setTrackingError("Could not save. Please try again.");
    } finally {
      setSavingTracking(false);
    }
  }

  function openEditStep(step: CadenceStep) {
    setEditStep(step);
    setEditOffsetDays(String(step.timing.offset_days ?? 0));
    setEditVariant(step.variant ?? "");
    setEditName(step.name ?? "");
    setEditDate(step.scheduled_send_date ?? "");
    setEditStepError(null);
  }

  async function handleSaveStep() {
    if (!campaignId || !editStep) return;
    const isNamed = isNamedChannel(editStep.channel);
    if (isNamed && !editName.trim()) {
      setEditStepError("Give the step a name.");
      return;
    }
    setSavingStep(true);
    setEditStepError(null);
    try {
      const updated = await dataAdapter.updateMarketingCampaignStep(
        campaignId,
        editStep.id,
        {
          timing_offset_days: parseInt(editOffsetDays, 10) || 0,
          content_variant: editVariant.trim() || null,
          ...(isNamed ? { name: editName.trim() } : {}),
          ...(isNamed && editDate ? { scheduled_send_date: editDate } : {}),
        },
      );
      setCampaign((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          steps: prev.steps.map((s) =>
            s.id === editStep.id ? { ...s, ...updated, order: s.order } : s,
          ),
        };
      });
      setEditStep(null);
      void reloadCampaign();
    } catch (e) {
      console.error("marketing.campaign.update_step.failed", e);
      setEditStepError(
        e instanceof ApiError && e.status === 400 && e.detail
          ? e.detail
          : "Could not save the step. Please try again.",
      );
    } finally {
      setSavingStep(false);
    }
  }

  async function handleDeleteStep() {
    if (!campaignId || !editStep) return;
    setDeletingStep(true);
    try {
      await dataAdapter.deleteMarketingCampaignStep(campaignId, editStep.id);
      setCampaign((prev) => {
        if (!prev) return prev;
        const steps = prev.steps.filter((s) => s.id !== editStep.id);
        return { ...prev, step_count: steps.length, steps };
      });
      setEditStep(null);
      void reloadCampaign();
    } catch (e) {
      console.error("marketing.campaign.delete_step.failed", e);
      setEditStepError("Could not delete the step. Please try again.");
    } finally {
      setDeletingStep(false);
    }
  }

  // An extra email tranche (the add-step dialog deliberately leaves email
  // out). The server places it after the existing tranches and refuses one
  // nobody would receive, with a reason worth showing as-is.
  const [addTrancheOpen, setAddTrancheOpen] = useState(false);
  const [trancheAudience, setTrancheAudience] = useState<"new" | "resend">(
    "new",
  );
  const [trancheOffsetDays, setTrancheOffsetDays] = useState("");
  // Tiers narrow the tranche to their people; with a re-send this is how one
  // tier gets a second, different email (each tier's own TIME then CONTROL).
  const [trancheTierIds, setTrancheTierIds] = useState<string[]>([]);
  const [addingTranche, setAddingTranche] = useState(false);
  const [addTrancheError, setAddTrancheError] = useState<string | null>(null);

  function openAddTranche() {
    setTrancheAudience("new");
    setTrancheOffsetDays("");
    setTrancheTierIds([]);
    setAddTrancheError(null);
    setAddTrancheOpen(true);
  }

  async function handleAddTranche() {
    if (!campaignId) return;
    setAddingTranche(true);
    setAddTrancheError(null);
    try {
      const offset = parseInt(trancheOffsetDays, 10);
      await dataAdapter.addMarketingCampaignStep(campaignId, {
        channel: "email",
        timing_offset_days: Number.isNaN(offset) ? undefined : offset,
        exclude_existing_members: trancheAudience === "new",
        target_tier_ids: trancheTierIds,
      });
      await reloadCampaign();
      setAddTrancheOpen(false);
    } catch (e) {
      console.error("marketing.campaign.add_tranche.failed", {
        status: e instanceof ApiError ? e.status : undefined,
      });
      setAddTrancheError(
        e instanceof ApiError && e.status === 400
          ? e.detail
          : "Could not add the tranche. Please try again.",
      );
    } finally {
      setAddingTranche(false);
    }
  }

  function openAddStep() {
    setNewChannel("call");
    setNewOffsetDays("0");
    setNewVariant("");
    setNewName("");
    setAddStepError(null);
    setAddStepOpen(true);
  }

  async function handleAddStep() {
    if (!campaignId) return;
    if (isNamedChannel(newChannel) && !newName.trim()) {
      setAddStepError("Give the step a name.");
      return;
    }
    setAddingStep(true);
    setAddStepError(null);
    try {
      await dataAdapter.addMarketingCampaignStep(campaignId, {
        channel: newChannel,
        timing_offset_days: parseInt(newOffsetDays, 10) || 0,
        content_variant: newVariant.trim() || undefined,
        ...(isNamedChannel(newChannel) ? { name: newName.trim() } : {}),
      });
      await reloadCampaign();
      setAddStepOpen(false);
    } catch (e) {
      console.error("marketing.campaign.add_step.failed", e);
      setAddStepError(
        e instanceof ApiError && e.status === 400 && e.detail
          ? e.detail
          : "Could not add the step. Please try again.",
      );
    } finally {
      setAddingStep(false);
    }
  }

  const sends = campaign ? emailSteps(campaign) : [];
  const shownPlan =
    campaign !== null && showsCampaignPlan(campaign, plan) ? plan : null;
  // A campaign showing its plan lists its mailings and call rounds in the
  // same date-ordered list as its sends; every other campaign lists sends.
  const sendListItems: CampaignSendListItem<CadenceStep>[] = shownPlan
    ? campaignSendListItems(sends, shownPlan.steps)
    : sendRows(sends).map((step) => ({ kind: "email", step }));
  const hasPlanRows = sendListItems.some((item) => item.kind === "plan");
  const otherCadenceSteps = campaign
    ? otherSteps(campaign, shownPlan !== null)
    : [];
  // Per the campaign-projects glossary: only surface tranche/Send language
  // once there are genuinely 2+ sends — a plain single-send campaign reads
  // exactly like the pre-this-feature workspace, no restructuring visible.
  const isSingleSend = sends.length <= 1 && !hasPlanRows;
  const sendsSent = sends.filter(hasSent).length;
  const displayStatus = campaign ? campaignDisplayStatus(campaign) : "draft";
  const showDeleteButton = campaign ? canDeleteCampaign(campaign) : false;
  const showArchiveButton = campaign ? canArchiveCampaign(campaign) : false;

  return (
    <div className="space-y-6">
      <MarketingNav active="campaigns" />

      <Link
        to={paths.marketingCampaigns()}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Back to campaigns
      </Link>

      {loading && (
        <div className="text-sm text-muted-foreground">Loading campaign…</div>
      )}

      {error && !loading && (
        <div className="text-sm p-8 text-center border border-border rounded-lg text-red-600">
          Couldn't load this campaign.
        </div>
      )}

      {!loading && !error && !campaign && (
        <div className="text-sm p-8 text-center border border-border rounded-lg text-muted-foreground">
          Campaign not found.
        </div>
      )}

      {!loading && !error && campaign && (
        <>
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl">{campaign.name}</h1>
                <Badge variant={campaignStatusVariant(displayStatus)}>
                  {campaignStatusLabel(displayStatus)}
                </Badge>
                {!isSingleSend && (
                  <span className="text-sm text-muted-foreground">
                    {sendsSent} of {sends.length} sent
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  onClick={handleOpenEditCampaign}
                  aria-label="Edit campaign details"
                >
                  <Pencil className="size-4" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                {[
                  campaignGoalLine(campaign),
                  fmtSendDate(campaign),
                  campaign.owner,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              {campaign.status === "legacy" && (
                <p className="text-sm text-muted-foreground mt-1">
                  Imported historical record — this campaign already happened
                  outside One and can't be activated or sent again.
                </p>
              )}
            </div>
            {showDeleteButton && (
              <div className="flex flex-col items-end gap-1">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="text-destructive hover:text-destructive"
                      disabled={deleting}
                    >
                      <Trash2 className="size-4" />
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Delete "{campaign.name}"?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        This permanently removes the draft campaign and its
                        cadence steps. It hasn't been activated, so no contacts
                        have been tagged in Mailchimp — this cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDeleteCampaign}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                {deleteError && (
                  <p className="text-xs text-red-600">{deleteError}</p>
                )}
              </div>
            )}
            {showArchiveButton && (
              <div className="flex flex-col items-end gap-1">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" disabled={archiving}>
                      <Archive className="size-4" />
                      Archive
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Archive "{campaign.name}"?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Hides this campaign from the campaign list. Its touches,
                        Mailchimp sync history, and results are kept — this is
                        not a delete.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleArchiveCampaign}>
                        Archive
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                {archiveError && (
                  <p className="text-xs text-red-600">{archiveError}</p>
                )}
              </div>
            )}
          </div>

          {/* Audience banner */}
          <Link
            to={paths.marketingAudienceBuilder({
              audienceId: campaign.audience_id,
            })}
            className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-surface px-4 py-3 hover:bg-surface-hover transition-colors"
          >
            <Users className="size-5 text-primary" />
            <span className="font-medium text-foreground">
              {campaign.audience_name}
            </span>
            {(campaign.member_count ?? 0) > 0 && (
              <span className="text-sm text-muted-foreground">
                {campaign.member_count} people · {campaign.suppressed_count}{" "}
                suppressed
              </span>
            )}
            <span className="ml-auto text-sm text-primary">
              View audience →
            </span>
          </Link>

          {shownPlan && (
            <>
              <TiersCard
                plan={shownPlan}
                onAddPeople={() => setAddPeopleOpen(true)}
                onViewPeople={() => setPeopleSheetOpen(true)}
              />
              {addedPeopleMessage && (
                <p
                  role="status"
                  className="rounded-md border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-950 px-3 py-2 text-sm text-green-900 dark:text-green-100"
                >
                  {addedPeopleMessage}
                </p>
              )}
            </>
          )}

          {/* End date — gates commercial-outcome attribution. Mailchimp
              linkage is per-send now, captured inside each send's own
              detail below. */}
          <div>
            <h2 className="text-lg font-semibold mb-1">Reporting</h2>
            <p className="text-sm text-muted-foreground mb-3">
              Commercial outcomes appear on the Results page once the end date
              has passed.
            </p>
            <div className="rounded-lg border border-border bg-surface p-4 space-y-4">
              <div className="max-w-xs space-y-1.5">
                <Label htmlFor="campaign-end-date">End date</Label>
                <Input
                  id="campaign-end-date"
                  type="date"
                  value={endDateInput}
                  onChange={(e) => setEndDateInput(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-3">
                <Button onClick={handleSaveTracking} disabled={savingTracking}>
                  {savingTracking ? "Saving…" : "Save"}
                </Button>
                {trackingSaved && !savingTracking && (
                  <span className="text-sm text-muted-foreground">Saved.</span>
                )}
                {trackingError && (
                  <span className="text-sm text-red-600">{trackingError}</span>
                )}
              </div>
            </div>
          </div>

          {/* Send(s) — a single-send campaign shows its detail directly,
              exactly like the pre-campaign-projects workspace. Two or more
              sends switch to the master-detail list. */}
          {campaign.status !== "legacy" && sendListItems.length > 0 && (
            <div>
              {!isSingleSend && (
                <div className="mb-3 space-y-1">
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="text-lg font-semibold">
                      {hasPlanRows
                        ? "Sends, mailings and call rounds"
                        : "Sends"}
                    </h2>
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleReorderSendsBySendDate}
                        disabled={reorderingSends}
                      >
                        {reorderingSends
                          ? "Re-ordering…"
                          : "Re-order by send date"}
                      </Button>
                    )}
                  </div>
                  {reorderSendsMessage && (
                    <p className="text-xs text-muted-foreground">
                      {reorderSendsMessage}
                    </p>
                  )}
                  {reorderSendsError && (
                    <p className="text-xs text-red-600">{reorderSendsError}</p>
                  )}
                </div>
              )}
              {isSingleSend ? (
                <CampaignSendDetail
                  campaignId={campaign.id}
                  step={sends[0]}
                  onStepChange={() => void reloadCampaign()}
                />
              ) : (
                <div className="space-y-2">
                  {sendListItems.map((item) => {
                    if (item.kind === "plan") {
                      const cadenceStep = campaign.steps.find(
                        (s) => s.id === item.step.id,
                      );
                      return (
                        <PlanStepRow
                          key={item.step.id}
                          campaignId={campaign.id}
                          step={item.step}
                          plan={shownPlan!}
                          expanded={expandedSendIds.has(item.step.id)}
                          onToggle={() => toggleSendExpanded(item.step.id)}
                          onChanged={() => void reloadCampaign()}
                          onEdit={() =>
                            cadenceStep && openEditStep(cadenceStep)
                          }
                        />
                      );
                    }
                    const step = item.step;
                    const label = sendLabel(step, sends, campaign.steps);
                    // Who a send goes to, on a campaign showing its plan.
                    const planSend =
                      shownPlan?.steps.find((p) => p.id === step.id) ?? null;
                    const parent = step.follows_step_id
                      ? sends.find((s) => s.id === step.follows_step_id)
                      : null;
                    // A reminder unlocks once its original tranche has
                    // sent (status !== draft) — matches
                    // generate_reminder_send's own precondition
                    // (original.status == active). No step ever reaches
                    // "closed" today, so gating on that instead left every
                    // reminder permanently locked.
                    const locked =
                      Boolean(parent) && parent?.status === "draft";
                    const canGenerate =
                      Boolean(parent) && !locked && step.status === "draft";
                    const hasReminder =
                      !step.follows_step_id &&
                      sends.some((s) => s.follows_step_id === step.id);
                    const canAddReminder =
                      !step.follows_step_id && !hasReminder;
                    const expanded = expandedSendIds.has(step.id);
                    return (
                      <div
                        key={step.id}
                        className="border border-border rounded-lg overflow-hidden"
                      >
                        <button
                          type="button"
                          onClick={() => toggleSendExpanded(step.id)}
                          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-surface-hover transition-colors"
                        >
                          <div
                            className={
                              shownPlan
                                ? "flex flex-wrap items-center gap-x-3 gap-y-1"
                                : "flex items-center gap-3"
                            }
                          >
                            {shownPlan && (
                              <ChannelIcon
                                channel="email"
                                className="size-4 text-foreground"
                              />
                            )}
                            <span className="font-medium text-foreground">
                              {label}
                            </span>
                            <Badge variant={sendStatusVariant(step)}>
                              {sendStatusLabel(step)}
                            </Badge>
                            {fmtStepSendDate(step) && (
                              <span className="text-sm text-muted-foreground">
                                {fmtStepSendDate(step)}
                              </span>
                            )}
                            {step.variant && (
                              <span className="text-sm text-muted-foreground">
                                {step.variant}
                              </span>
                            )}
                            {shownPlan && (
                              <SendAudienceLine
                                planSend={planSend}
                                plan={shownPlan}
                              />
                            )}
                          </div>
                          <ChevronRight
                            className={`size-4 text-muted-foreground transition-transform ${
                              expanded ? "rotate-90" : ""
                            }`}
                          />
                        </button>
                        {expanded && (
                          <div className="border-t border-border px-4 py-4">
                            {locked ? (
                              <div className="space-y-3">
                                <p className="text-sm text-muted-foreground">
                                  Waiting on{" "}
                                  {sendLabel(parent!, sends, campaign.steps)} to
                                  send before this reminder's audience can be
                                  prepared.
                                </p>
                                <div className="space-y-1.5 max-w-64">
                                  <Label>Reminder gap</Label>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-foreground">
                                      {step.timing.offset_days ?? 7} days
                                    </span>
                                    <span className="text-sm text-muted-foreground">
                                      after{" "}
                                      {sendLabel(
                                        parent!,
                                        sends,
                                        campaign.steps,
                                      )}{" "}
                                      sends
                                    </span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openEditStep(step)}
                                    >
                                      Edit
                                    </Button>
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    Its real send date isn&apos;t known yet —
                                    only this gap is. Once{" "}
                                    {sendLabel(parent!, sends, campaign.steps)}{" "}
                                    actually sends, this reminder&apos;s date is
                                    set to that real date plus the gap above.
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <>
                                {canAddReminder && (
                                  <div className="mb-4 flex items-center gap-3">
                                    <Button
                                      variant="outline"
                                      onClick={() => handleAddReminder(step.id)}
                                      disabled={
                                        addingReminderForStepId === step.id
                                      }
                                    >
                                      {addingReminderForStepId === step.id
                                        ? "Adding…"
                                        : "Add reminder"}
                                    </Button>
                                    {addReminderError && (
                                      <span className="text-sm text-red-600">
                                        {addReminderError}
                                      </span>
                                    )}
                                  </div>
                                )}
                                {canGenerate && (
                                  <div className="mb-4 flex items-center gap-3">
                                    <Button
                                      variant="outline"
                                      onClick={() =>
                                        handleGenerateReminder(
                                          step.id,
                                          parent!.id,
                                        )
                                      }
                                      disabled={
                                        generatingReminderStepId === step.id
                                      }
                                    >
                                      {generatingReminderStepId === step.id
                                        ? "Preparing…"
                                        : "Generate reminder audience"}
                                    </Button>
                                    {generateReminderError && (
                                      <span className="text-sm text-red-600">
                                        {generateReminderError}
                                      </span>
                                    )}
                                  </div>
                                )}
                                {/* On a campaign showing its plan, a
                                    tranche may go to chosen tiers only;
                                    with none chosen it goes to everyone in
                                    the audience, as on any campaign. */}
                                {shownPlan &&
                                  planSend &&
                                  !planSend.follows_step_id && (
                                    <div className="mb-4">
                                      <StepTiersEditor
                                        key={planSend.target_tier_ids.join(",")}
                                        campaignId={campaign.id}
                                        step={planSend}
                                        tiers={shownPlan.tiers}
                                        onChanged={() => void reloadCampaign()}
                                      />
                                    </div>
                                  )}
                                <CampaignSendDetail
                                  campaignId={campaign.id}
                                  step={step}
                                  onStepChange={() => void reloadCampaign()}
                                />
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Cadence — other-channel steps (call/enclosure/direct_mail),
              unrelated to the tranche/reminder mechanism above. Legacy
              campaigns have no real send plan, only the Mailchimp/engagement
              data pulled in at import, so there's nothing meaningful to show
              or edit here. */}
          {campaign.status === "legacy" ? (
            <div className="rounded-lg border border-border bg-surface px-4 py-3 text-sm text-muted-foreground">
              No cadence — this campaign's history was imported directly from
              Mailchimp, not built or sent through One.
            </div>
          ) : (
            otherCadenceSteps.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-3">Cadence</h2>
                <div className="space-y-1">
                  {otherCadenceSteps.map((step, idx) => {
                    const Icon = CHANNEL_ICONS[step.channel];
                    return (
                      <div key={step.id}>
                        <button
                          onClick={() => openEditStep(step)}
                          className="w-full text-left rounded-lg border border-border bg-surface px-4 py-3 hover:bg-surface-hover transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                              {step.order}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-xs font-medium text-muted-foreground bg-surface-hover rounded px-1.5 py-0.5">
                                  {step.timing.label}
                                </span>
                                <Icon className="size-4 text-foreground" />
                                <span className="font-medium text-foreground">
                                  {CHANNEL_LABELS[step.channel]}
                                </span>
                                <Badge variant="outline">
                                  {CHANNEL_TARGETS[step.channel]}
                                </Badge>
                              </div>
                              {step.variant && (
                                <p className="text-sm text-foreground mt-1">
                                  {step.variant}
                                </p>
                              )}
                              <p className="text-sm text-muted-foreground mt-0.5">
                                {step.will_send_count > 0
                                  ? `${step.will_send_count} will send · ${step.skipped_count} skipped (no ${step.consent_gate.replace("_", " ")} consent)`
                                  : "Counts not yet available"}
                              </p>
                            </div>
                          </div>
                        </button>
                        {idx < otherCadenceSteps.length - 1 && (
                          <div className="flex justify-center py-1">
                            <ArrowDown className="size-4 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )
          )}

          {/* Same rule as add_cadence_step: anything but an archived project
              or a read-only legacy import can gain a send or step. */}
          {campaign.status !== "archived" && campaign.status !== "legacy" && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={openAddTranche}>
                <Plus className="size-4" />
                Add tranche
              </Button>
              <Button
                variant="outline"
                onClick={
                  shownPlan ? () => setAddPlanStepOpen(true) : openAddStep
                }
              >
                <Plus className="size-4" />
                Add step
              </Button>
            </div>
          )}
        </>
      )}

      {/* Step edit drawer — the generic "Cadence" list (non-email steps) */}
      <Sheet
        open={editStep !== null}
        onOpenChange={(o) => !o && setEditStep(null)}
      >
        <SheetContent className="w-full sm:max-w-md">
          {editStep && (
            <>
              <SheetHeader>
                <SheetTitle>Edit step {editStep.order}</SheetTitle>
                <SheetDescription>
                  Timing and content variant. The channel can't change: delete
                  the step and add another instead.
                </SheetDescription>
              </SheetHeader>
              <div className="space-y-4 px-4">
                <div className="space-y-1.5">
                  <Label>Channel</Label>
                  <p className="text-sm text-foreground">
                    {CHANNEL_LABELS[editStep.channel]}
                  </p>
                </div>
                {isNamedChannel(editStep.channel) && (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor="step-name">Name</Label>
                      <Input
                        id="step-name"
                        value={editName}
                        maxLength={120}
                        onChange={(e) => setEditName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5 max-w-48">
                      <Label htmlFor="step-date">Date</Label>
                      <Input
                        id="step-date"
                        type="date"
                        value={editDate}
                        onChange={(e) => setEditDate(e.target.value)}
                      />
                    </div>
                  </>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="step-timing">Timing (days offset)</Label>
                  <Input
                    id="step-timing"
                    type="number"
                    min="0"
                    value={editOffsetDays}
                    onChange={(e) => setEditOffsetDays(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="step-variant">Content variant</Label>
                  <Input
                    id="step-variant"
                    value={editVariant}
                    onChange={(e) => setEditVariant(e.target.value)}
                    placeholder="e.g. Leaflet B, Email template A"
                  />
                </div>
                {editStep.will_send_count > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {editStep.will_send_count} will send ·{" "}
                    {editStep.skipped_count} skipped at this gate.
                  </p>
                )}
                {editStepError && (
                  <p className="text-sm text-red-600" role="alert">
                    {editStepError}
                  </p>
                )}
              </div>
              <SheetFooter className="flex-col gap-2 sm:flex-row">
                <Button
                  variant="destructive"
                  className="sm:mr-auto"
                  onClick={handleDeleteStep}
                  disabled={deletingStep || savingStep}
                >
                  {deletingStep ? "Deleting…" : "Delete step"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setEditStep(null)}
                  disabled={savingStep || deletingStep}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveStep}
                  disabled={savingStep || deletingStep}
                >
                  {savingStep ? "Saving…" : "Save step"}
                </Button>
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Edit campaign name/goal/description dialog */}
      <Dialog open={editCampaignOpen} onOpenChange={setEditCampaignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit campaign</DialogTitle>
            <DialogDescription>
              Update the campaign's name, goal, description, audience and
              promoted product.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="campaign-name">Name</Label>
              <Input
                id="campaign-name"
                value={editNameInput}
                onChange={(e) => setEditNameInput(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="campaign-goal">Goal</Label>
              <Select
                value={editGoalInput}
                onValueChange={(v) => setEditGoalInput(v as CampaignGoal)}
              >
                <SelectTrigger id="campaign-goal">
                  <SelectValue placeholder="Choose a goal…" />
                </SelectTrigger>
                <SelectContent>
                  {CAMPAIGN_GOALS.map((goal) => (
                    <SelectItem key={goal} value={goal}>
                      {CAMPAIGN_GOAL_LABELS[goal]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="campaign-description">
                Description (optional)
              </Label>
              <Input
                id="campaign-description"
                value={editDescriptionInput}
                onChange={(e) => setEditDescriptionInput(e.target.value)}
              />
            </div>
            {campaign && (
              <div className="space-y-1.5">
                <Label htmlFor="campaign-audience">Audience</Label>
                {canChangeCampaignAudience(campaign) ? (
                  <Select
                    value={editAudienceId}
                    onValueChange={setEditAudienceId}
                  >
                    <SelectTrigger
                      id="campaign-audience"
                      aria-describedby="campaign-audience-hint"
                    >
                      <SelectValue placeholder="Choose an audience…" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* The current audience stays selectable even before
                          the list loads (or if it falls outside the first
                          page). */}
                      {!audienceOptions.some(
                        (a) => a.id === campaign.audience_id,
                      ) && (
                        <SelectItem value={campaign.audience_id}>
                          {campaign.audience_name}
                        </SelectItem>
                      )}
                      {audienceOptions.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Select value={campaign.audience_id} disabled>
                    <SelectTrigger
                      id="campaign-audience"
                      aria-describedby="campaign-audience-hint"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={campaign.audience_id}>
                        {campaign.audience_name}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <p
                  id="campaign-audience-hint"
                  className="text-xs text-muted-foreground"
                >
                  {canChangeCampaignAudience(campaign)
                    ? "Every send has gone out, so this only changes which audience the campaign and its results are reported against. Nobody is re-sent anything."
                    : "Can be changed once every send has gone out — the people each send goes to were fixed from this audience when the campaign was set up."}
                </p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label id="campaign-product-categories-label">
                Product categories (optional)
              </Label>
              <div
                role="group"
                aria-labelledby="campaign-product-categories-label"
                aria-describedby="campaign-product-categories-hint"
                className="flex flex-wrap gap-1.5"
              >
                {SELECTABLE_MARKETING_CATEGORIES.map((cat) => (
                  <Toggle
                    key={cat.value}
                    variant="outline"
                    size="sm"
                    pressed={editMarketingCategories.includes(cat.value)}
                    onPressedChange={(pressed) =>
                      setEditMarketingCategories((prev) =>
                        pressed
                          ? [...prev, cat.value]
                          : prev.filter((c) => c !== cat.value),
                      )
                    }
                    className="text-xs px-2"
                  >
                    {cat.label}
                  </Toggle>
                ))}
              </div>
              <p
                id="campaign-product-categories-hint"
                className="text-xs text-muted-foreground"
              >
                If this campaign promotes particular kinds of product, pick
                them. The Results page will then count only orders and quotes in
                these categories, and compare them with what the same customers
                normally order in them. Leave empty to count every order.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="campaign-product-keywords">
                Product keywords (optional)
              </Label>
              <Input
                id="campaign-product-keywords"
                placeholder="e.g. advent, advent calendar"
                value={editProductKeywordsInput}
                onChange={(e) => setEditProductKeywordsInput(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                If this campaign promotes a specific product, list a few
                comma-separated words for it. The Results page will then scope
                commercial outcomes to orders matching one of these words,
                instead of any order from the campaign's cohort. With categories
                as well, an order must match both.
              </p>
            </div>
            {editCampaignError && (
              <p className="text-xs text-red-600">{editCampaignError}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditCampaignOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveCampaignInfo}
              disabled={savingCampaignInfo}
            >
              {savingCampaignInfo ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add tranche dialog — another email send to the campaign's audience */}
      <Dialog open={addTrancheOpen} onOpenChange={setAddTrancheOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add an email tranche</DialogTitle>
            <DialogDescription>
              Another email send to this campaign's audience, as it stands now.
              Like every tranche, it gets its own Mailchimp campaign and can
              have its own reminder.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Who should it go to?</Label>
              <RadioGroup
                value={trancheAudience}
                onValueChange={(v) => setTrancheAudience(v as "new" | "resend")}
              >
                <div className="flex items-start gap-2">
                  <RadioGroupItem
                    value="new"
                    id="tranche-new"
                    className="mt-0.5"
                  />
                  <Label htmlFor="tranche-new" className="font-normal">
                    People not yet sent this campaign — for example, people
                    added to the audience since it was set up
                  </Label>
                </div>
                <div className="flex items-start gap-2">
                  <RadioGroupItem
                    value="resend"
                    id="tranche-resend"
                    className="mt-0.5"
                  />
                  <Label htmlFor="tranche-resend" className="font-normal">
                    Everyone in the audience, including people already sent a
                    tranche (a deliberate re-send)
                  </Label>
                </div>
              </RadioGroup>
            </div>
            {shownPlan && shownPlan.tiers.length > 0 && (
              <div className="space-y-2">
                <Label>Only these tiers (optional)</Label>
                <TierCheckboxes
                  idPrefix="tranche-tier"
                  tiers={shownPlan.tiers}
                  selected={trancheTierIds}
                  onChange={setTrancheTierIds}
                />
                <p className="text-sm text-muted-foreground">
                  Leave every tier unticked to send to the people chosen above.
                  Tick tiers to send only to their people — with a re-send, that
                  includes people in those tiers already sent a tranche.
                </p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="tranche-offset">
                Timing offset (days from start, optional)
              </Label>
              <Input
                id="tranche-offset"
                type="number"
                min={0}
                value={trancheOffsetDays}
                onChange={(e) => setTrancheOffsetDays(e.target.value)}
              />
            </div>
            {addTrancheError && (
              <p className="text-sm text-red-600" role="alert">
                {addTrancheError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddTrancheOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddTranche} disabled={addingTranche}>
              {addingTranche ? "Adding…" : "Add tranche"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add step dialog */}
      <Dialog open={addStepOpen} onOpenChange={setAddStepOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add cadence step</DialogTitle>
            <DialogDescription>
              Choose a channel and timing offset for the new step. For another
              email send, use "Add tranche", or a tranche's "Add reminder"
              action.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Channel</Label>
              <Select
                value={newChannel}
                onValueChange={(v) => setNewChannel(v as MarketingChannel)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHANNELS.map((ch) => (
                    <SelectItem key={ch.value} value={ch.value}>
                      {ch.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isNamedChannel(newChannel) && (
              <div className="space-y-1.5">
                <Label htmlFor="new-step-name">Name</Label>
                <Input
                  id="new-step-name"
                  value={newName}
                  maxLength={120}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Call round – spring follow-up"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="step-offset">
                Timing offset (days from start)
              </Label>
              <Input
                id="step-offset"
                type="number"
                min={0}
                value={newOffsetDays}
                onChange={(e) => setNewOffsetDays(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="step-variant">Content variant (optional)</Label>
              <Input
                id="step-variant"
                placeholder="e.g. Spring offer — Template A"
                value={newVariant}
                onChange={(e) => setNewVariant(e.target.value)}
              />
            </div>
            {addStepError && (
              <p className="text-sm text-red-600" role="alert">
                {addStepError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddStepOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddStep} disabled={addingStep}>
              {addingStep ? "Adding…" : "Add step"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {campaign && shownPlan && (
        <>
          <AddPlanStepDialog
            campaignId={campaign.id}
            plan={shownPlan}
            open={addPlanStepOpen}
            onOpenChange={setAddPlanStepOpen}
            onAdded={() => void reloadCampaign()}
          />
          <CampaignPeopleSheet
            campaignId={campaign.id}
            tiers={shownPlan.tiers}
            open={peopleSheetOpen}
            onOpenChange={setPeopleSheetOpen}
            refreshKey={peopleRefreshKey}
            onAddPeople={() => setAddPeopleOpen(true)}
          />
          <AddPeopleDialog
            campaignId={campaign.id}
            audienceName={shownPlan.audience_name ?? campaign.audience_name}
            tiers={shownPlan.tiers}
            open={addPeopleOpen}
            onOpenChange={setAddPeopleOpen}
            onAdded={(result) => {
              const people = (n: number) =>
                `${n} ${n === 1 ? "person" : "people"}`;
              setAddedPeopleMessage(
                `${people(result.tiered_count)} now ${result.tiered_count === 1 ? "has a tier" : "have tiers"} in this campaign` +
                  (result.added_to_audience_count > 0
                    ? `, ${result.added_to_audience_count} of them also added to the ${shownPlan.audience_name ?? campaign.audience_name} audience.`
                    : "."),
              );
              setPeopleRefreshKey((k) => k + 1);
              void reloadCampaign();
            }}
          />
        </>
      )}
    </div>
  );
}

/** Under a send's row on a campaign showing its plan: when, who it goes to
 *  and how many. A Reminder, which follows its tranche, shows the same. */
function SendAudienceLine({
  planSend,
  plan,
}: {
  planSend: CampaignPlanStep | null;
  plan: CampaignPlan;
}) {
  if (!planSend) return null;
  return (
    <span className="w-full pl-7 text-sm text-muted-foreground">
      {stepAudienceLabel(planSend, plan.tiers, plan.steps)} ·{" "}
      <RecipientCount count={planSend.recipient_count} />
    </span>
  );
}

function RecipientCount({ count }: { count: number }) {
  return count === 0 ? (
    <span className="text-amber-800 dark:text-amber-300">
      No recipients yet
    </span>
  ) : (
    <>{recipientCountLabel(count)}</>
  );
}

/** A mailing or call round in the campaign's list of sends: collapsed, its
 *  date, who it goes to, how many and its state; opened, its own detail. */
function PlanStepRow({
  campaignId,
  step,
  plan,
  expanded,
  onToggle,
  onChanged,
  onEdit,
}: {
  campaignId: string;
  step: CampaignPlanStep;
  plan: CampaignPlan;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => void;
  onEdit: () => void;
}) {
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-surface-hover transition-colors"
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <ChannelIcon
            channel={step.channel}
            className="size-4 text-foreground"
          />
          <span className="font-medium text-foreground">
            {step.name ?? step.label}
          </span>
          <StepStateBadge state={step.state} />
          <span className="w-full pl-7 text-sm text-muted-foreground">
            {formatDateOrDash(step.date)} ·{" "}
            {stepAudienceLabel(step, plan.tiers, plan.steps)} ·{" "}
            <RecipientCount count={step.recipient_count} />
          </span>
        </div>
        <ChevronRight
          className={`size-4 shrink-0 text-muted-foreground transition-transform ${
            expanded ? "rotate-90" : ""
          }`}
        />
      </button>
      {expanded && (
        <div className="border-t border-border px-4 py-4">
          <PlanStepDetail
            campaignId={campaignId}
            step={step}
            tiers={plan.tiers}
            onChanged={onChanged}
            onEdit={onEdit}
          />
        </div>
      )}
    </div>
  );
}
